import _ from 'underscore';
import React, { useMemo, useCallback } from 'react';
import { useResponsiveSize } from '@bingads-webui-react/with-responsive-size';
import { StepProgressList, TopStepProgressBar } from '@bingads-webui-fluent-component/step-progress-indicator';
import {
  useWizardService,
  getCurrentStepId,
  getCurrentFlow,
  getIsStepVisited,
  getIsStepValid,
  getIsStepSaveFailed,
  WIZARD_EVENTS,
} from '@bingads-webui-react/wizard-core';

interface StepperProps {
  config?: Array<{
    id: string;
    title?: string;
    detail?: string;
    steps?: Array<{
      id: string;
      title: string;
      detail?: string;
      isBehind?: boolean;
    }>;
  }>;
  getOnItemClickFn?: (step: any) => any;
}

const getStepConfigWithStatus = (config, state) => {
  const returnConfig = [];
  _.each(config, (s) => {
    const isVisited = getIsStepVisited(state, s.id);
    const isValid = getIsStepValid(state, s.id);
    const isSaveFailed = getIsStepSaveFailed(state, s.id);
    const newStepConfig = {
      ...s,
      isVisited,
      hasError: !isValid && isSaveFailed,
    };
    if (!_.isEmpty(s.steps)) {
      newStepConfig.steps = getStepConfigWithStatus(s.steps, state);
    }
    returnConfig.push(newStepConfig);
  });
  return returnConfig;
};

const withStepFlow = (Component) => {
  const HoC: React.FC<StepperProps> = (props) => {
    const { config, getOnItemClickFn } = props;
    const { state, send } = useWizardService();
    const currentStepId = getCurrentStepId(state);
    const currentFlow = getCurrentFlow(state);
    const displayMode = useResponsiveSize();
    const isTitleCollapsed = ['l', 'xl'].includes(displayMode);

    const stepListConfig = useMemo(() => {
      if (!config) {
        return currentFlow.map((id) => ({ id, title: id }));
      }
      return getStepConfigWithStatus(config, state);
    }, [config, currentFlow, state]);

    const getDefaultOnItemClickFn = useCallback((step) => {
      let toStep;
      if (currentFlow.includes(step.id)) {
        toStep = step;
      } else if (!_.isEmpty(step.steps)) {
        const firstSubStep = _.find(step.steps, (s) => currentFlow.includes(s.id));
        if (firstSubStep) {
          toStep = firstSubStep;
        }
      }
      if (toStep && toStep.isBehind) {
        return () => {
          send(WIZARD_EVENTS.NAVIGATE_TO, {
            stepId: toStep.id,
          });
        };
      }
      return undefined;
    }, [currentFlow, send]);

    return (
      <Component
        {...props}
        isTitleCollapsed={isTitleCollapsed}
        config={stepListConfig}
        currentStepId={currentStepId}
        currentFlow={currentFlow}
        getOnItemClickFn={getOnItemClickFn || getDefaultOnItemClickFn}
      />
    );
  };
  return HoC;
};

export const StepList: React.FC<StepperProps> = withStepFlow(StepProgressList);
export const StepBar: React.FC<StepperProps> = withStepFlow(TopStepProgressBar);
